// Firebase Cloud Messaging Configuration File.
// Read more at https://firebase.google.com/docs/cloud-messaging/js/client && https://firebase.google.com/docs/cloud-messaging/js/receive

import { initializeApp } from "@firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "@firebase/messaging";
import ApiMethods from "./ApiManager/ApiMethods";

var firebaseConfig = {
  apiKey: "AIzaSyDOg-x942QO_VNXeAOQDTVruYPrjTr4qsA",
  authDomain: "hit-fit-5eac2.firebaseapp.com",
  projectId: "hit-fit-5eac2",
  storageBucket: "hit-fit-5eac2.appspot.com", // Fix typo: `.app` should be `.com`
  messagingSenderId: "268816991166",
  appId: "1:268816991166:web:67ba1ccabff3855e1fefef",
  measurementId: "G-ZQ41JCJ7VN",
};

const firebase = initializeApp(firebaseConfig);
console.log(firebase);

// getMessaging() throws synchronously in environments that don't support the Firebase
// Messaging APIs (some browsers, private/incognito modes, blocked service workers) — since this
// file is imported at app startup, an unguarded throw here used to crash the entire app before
// anything rendered. isSupported() + a try/catch keep that failure contained to "no push
// notifications this session" instead.
let messaging = null;
const messagingReady = isSupported()
  .then((supported) => {
    if (supported) {
      try {
        messaging = getMessaging();
      } catch (err) {
        console.error("Failed to initialize Firebase Messaging:", err);
      }
    }
  })
  .catch((err) => {
    console.error("Firebase Messaging support check failed:", err);
  });

// let userUid = null;

// onAuthStateChanged(auth, (user) => {
//   if (user) {
//     userUid = user.uid; // Get the UID
//     console.log("User UID: ", userUid);
//   } else {
//     console.log("No user is signed in.");
//   }
// });

const VAPID_KEY =
  "BJEU_5R_zbMc3wzCzOn66I_hNbdU_UOiZyoat8Ps6a5SpgXQay77zGqDce6NvHrMIMJAPrK0oRO9rBFbuMVEDjc";

// Gets this browser's FCM token and registers it with the API for the logged-in employee, so
// every employee notification can be pushed here (see api/src/services/push.service.ts).
// `prompt: true` may show the browser's permission dialog — only pass it from a user gesture
// (Login's submit); `prompt: false` (app load) only refreshes the token when permission is
// already granted. Never throws: a denied permission or unsupported browser just means no push.
export const registerPushToken = async ({ prompt = false } = {}) => {
  try {
    await messagingReady;
    const authToken = localStorage.getItem("token");
    if (!messaging || !authToken || typeof Notification === "undefined") return false;

    if (Notification.permission === "default" && prompt) {
      await Notification.requestPermission();
    }
    if (Notification.permission !== "granted") return false;

    const registration = await navigator.serviceWorker.ready;
    const currentToken = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!currentToken) return false;

    // Re-register when the token changed OR a different employee is logged in on this browser
    // (the server moves a token to whoever registers it last).
    const employeeId = localStorage.getItem("id");
    const cacheKey = `${employeeId}:${currentToken}`;
    if (localStorage.getItem("fcm_registered") === cacheKey) return true;

    await ApiMethods.post("notifications/push-token", { token: currentToken }, authToken);
    localStorage.setItem("fcm_token", currentToken);
    localStorage.setItem("fcm_registered", cacheKey);
    return true;
  } catch (err) {
    // Deliberately swallowed (denied permission, blocked service worker, offline, ...).
    console.warn("Push notification registration unavailable: ", err);
    return false;
  }
};

// What the Profile screen shows for THIS browser/device:
//   "unsupported" — no push APIs here (insecure context, unsupported browser)
//   "denied"      — the user blocked notifications; only browser settings can undo that
//   "default"     — never asked yet (subscribing will show the permission dialog)
//   "unregistered"— permission is granted but this device isn't registered for this employee
//   "subscribed"  — granted and registered for the logged-in employee
export const getPushStatus = async () => {
  await messagingReady;
  if (!messaging || typeof Notification === "undefined" || !("serviceWorker" in navigator)) {
    return "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "default") return "default";

  const cached = localStorage.getItem("fcm_registered") || "";
  const employeeId = localStorage.getItem("id");
  return cached.startsWith(`${employeeId}:`) ? "subscribed" : "unregistered";
};

// Called on logout so a shared browser stops receiving the previous employee's pushes.
export const unregisterPushToken = async () => {
  try {
    const currentToken = localStorage.getItem("fcm_token");
    const authToken = localStorage.getItem("token");
    localStorage.removeItem("fcm_registered");
    if (currentToken && authToken) {
      // Bounded so a slow/offline server can never stall the logout that awaits this.
      await Promise.race([
        ApiMethods.post("notifications/push-token/remove", { token: currentToken }, authToken),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    }
  } catch (err) {
    console.warn("Push token removal failed: ", err);
  }
};

// `navigator.serviceWorker` only exists in a secure context (https:// or http://localhost) —
// opening the app over plain HTTP via a LAN IP (e.g. http://192.168.1.15:3002) is not one, so
// this was `undefined` and `.register` threw synchronously at import time, crashing the whole
// app before anything rendered. Guard it the same way the Messaging support check above does:
// no push notifications this session instead of no app at all.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/firebase-messaging-sw.js")
    .then((registration) => {
      localStorage.setItem(
        "firebase",
        JSON.stringify({ scope: registration.scope })
      );
      console.log("firebase-messaging registered:", registration);
    })
    .catch((err) => {
      localStorage.setItem("firebase", "Firebase not set");
      console.error("firebase-messaging registration failed:", err);
    });
} else {
  console.warn(
    "navigator.serviceWorker unavailable (insecure context — not https:// or localhost); push notifications disabled this session."
  );
}

export const onMessageListener = () =>
  new Promise((resolve) => {
    if (!messaging) {
      return;
    }
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
